import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { App, Button, Input, Result, Space, Switch, Tooltip, Typography } from 'antd';
import { Check, Copy, Database, Link, Search, Tag, Zap } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { FullScreenSpinner } from '@/components/FullScreenSpinner';
import { EditableTitle } from '@/components/EditableTitle';
import { Pager } from '@/components/Pager';
import { usePagination, usePageBackLink } from '@/hooks';
import { AdminAssessmentPackagesService, AdminQuestionSetsService, BaseAPIClient } from '@/services/api';
import type { AssessmentPackage, QuestionSet } from '@/services/api';
import { buildPackageBuyUrl, containerVariants, itemVariants } from '@/utils';
import { APP_CURRENCY } from '@/utils/constants/currency';
import { ROUTES } from '@/routes';
import { AccessCodesCard } from './AccessCodesCard';
import styles from './AdminPackages.module.scss';

const { Text } = Typography;

const PICKER_PER_PAGE = 9;

interface PackageEditorProps {
  /** Absent for the "new" route. */
  id?: string;
}

interface Draft {
  name: string;
  description: string;
  unlockPriceDollars: number;
  unlockIncludedCredits: number;
  interviewCostCredits: number;
  questionSetId: string | null;
  coachingEnabled: boolean;
}

const questionSetStats = (qs: QuestionSet) => {
  const competencies = new Set(qs.items.map((item) => item.question.competencyId));
  return { questions: qs.items.length, competencies: competencies.size };
};

const digitsOnly = (value: string) => Math.max(0, parseInt(value.replace(/[^0-9]/g, ''), 10) || 0);

interface CreditBoxProps {
  label: string;
  icon: React.ReactNode;
  tint: 'purple' | 'periwinkle' | 'green';
  value: string;
  unit: string;
  onChange: (raw: string) => void;
  onBlur?: () => void;
}

const CreditBox: React.FC<CreditBoxProps> = ({ label, icon, tint, value, unit, onChange, onBlur }) => (
  <div className={styles.creditBox}>
    <div className={styles.creditBoxTop}>
      <div className={`${styles.creditIcon} ${styles[`tint-${tint}`]}`}>{icon}</div>
      <span className={styles.creditBoxLabel}>{label}</span>
    </div>
    <div className={styles.creditInputRow}>
      <input
        className={styles.creditInput}
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        size={Math.max(1, value.length)}
      />
      <span className={styles.creditUnit}>{unit}</span>
    </div>
  </div>
);

export const PackageEditor: React.FC<PackageEditorProps> = ({ id }) => {
  const { getToken } = useAuth();
  const { notification, modal } = App.useApp();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [questionSets, setQuestionSets] = useState<QuestionSet[]>([]);
  const [existing, setExisting] = useState<AssessmentPackage | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [draft, setDraft] = useState<Draft>({
    name: '',
    description: '',
    unlockPriceDollars: 20,
    unlockIncludedCredits: 200,
    interviewCostCredits: 10,
    questionSetId: null,
    coachingEnabled: true,
  });

  const [priceText, setPriceText] = useState('20.00');

  const [pickSearch, setPickSearch] = useState('');

  usePageBackLink(() => navigate(ROUTES.ADMIN_PACKAGES), 'Back to Packages');

  const getServices = useCallback(async () => {
    const token = await getToken();
    if (!token) throw new Error('Not authenticated');
    return {
      packages: new AdminAssessmentPackagesService(token),
      questionSets: new AdminQuestionSetsService(token),
    };
  }, [getToken]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const services = await getServices();
      const [questionSetList, current] = await Promise.all([
        services.questionSets.list(),
        // Only a 404 on the package itself means "deleted". Catching here
        // rather than around the whole Promise.all keeps an auth failure, a
        // server error, or a failed question-set list from being reported as
        // a missing package with no error and no retry path.
        id
          ? services.packages.getById(id).catch((err: unknown) => {
              if (BaseAPIClient.isApiError(err) && err.status === 404) return null;
              throw err;
            })
          : Promise.resolve(null),
      ]);
      setQuestionSets(questionSetList);
      setExisting(current);
      setNotFound(Boolean(id) && !current);
      if (current) {
        setDraft({
          name: current.name,
          description: current.description ?? '',
          unlockPriceDollars: current.unlockPriceCents / 100,
          unlockIncludedCredits: current.unlockIncludedCredits,
          interviewCostCredits: current.interviewCostCredits,
          questionSetId: current.questionSetId,
          coachingEnabled: current.coachingEnabled,
        });
        setPriceText((current.unlockPriceCents / 100).toFixed(2));
      }
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    } finally {
      setLoading(false);
    }
  }, [getServices, id, notification]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const filteredQuestionSets = useMemo(() => {
    const term = pickSearch.trim().toLowerCase();
    return questionSets.filter((qs) => {
      if (!term) return true;
      return qs.name.toLowerCase().includes(term);
    });
  }, [questionSets, pickSearch]);

  const {
    page: pickerPage,
    pageCount: pickerPages,
    pageItems: pickerItems,
    from: pickerFrom,
    to: pickerTo,
    total: pickerTotal,
    setPage: setPickPage,
  } = usePagination(filteredQuestionSets, PICKER_PER_PAGE);

  const selectedQuestionSet = draft.questionSetId
    ? questionSets.find((qs) => qs.id === draft.questionSetId) ?? null
    : null;

  const save = async () => {
    if (!draft.questionSetId) {
      notification.error({ message: 'A question set is required' });
      return;
    }

    setSaving(true);
    try {
      const services = await getServices();
      const payload = {
        name: draft.name.trim() || 'Untitled Package',
        description: draft.description.trim() || undefined,
        unlockPriceCents: Math.round(draft.unlockPriceDollars * 100),
        unlockIncludedCredits: draft.unlockIncludedCredits,
        interviewCostCredits: draft.interviewCostCredits,
        questionSetId: draft.questionSetId,
        coachingEnabled: draft.coachingEnabled,
      };
      if (existing) {
        await services.packages.update(existing.id, payload);
      } else {
        await services.packages.create(payload);
      }
      notification.success({ message: 'Package saved' });
      navigate(ROUTES.ADMIN_PACKAGES);
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existing) return;
    try {
      const services = await getServices();
      await services.packages.remove(existing.id);
      notification.success({ message: 'Package deleted' });
      navigate(ROUTES.ADMIN_PACKAGES);
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    }
  };

  const copyBuyLink = async () => {
    if (!existing) return;
    try {
      await navigator.clipboard.writeText(buildPackageBuyUrl(existing.id));
      notification.success({ message: 'Purchase link copied' });
    } catch {
      notification.error({ message: 'Could not copy link' });
    }
  };

  const confirmDelete = () => {
    modal.confirm({
      title: 'Delete this package?',
      icon: null,
      maskClosable: true,
      centered: true,
      content:
        'Unused access codes are deleted with it. Codes that have been used stay, along with the results behind them. This cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: () => handleDelete(),
    });
  };

  if (loading) return <FullScreenSpinner />;

  if (notFound) {
    return (
      <Result
        status="404"
        title="Package not found"
        subTitle="This package may have been deleted, or the link is wrong."
        extra={
          <Button type="primary" onClick={() => navigate(ROUTES.ADMIN_PACKAGES)}>
            Back to packages
          </Button>
        }
      />
    );
  }

  const canSave = Boolean(draft.questionSetId);

  return (
    <motion.div className={styles.editorPage} variants={containerVariants} initial="hidden" animate="visible">
      <motion.div variants={itemVariants}>
      <div className={styles.editorHead}>
        <div className={styles.nameRow}>
          <EditableTitle
            value={draft.name}
            onChange={(value) => setDraft((d) => ({ ...d, name: value }))}
            placeholder="Package name…"
            textClassName={styles.nameInput}
          />
        </div>
        <Space>
          {existing && (
            <Button className={styles.btnSecondaryDanger} onClick={confirmDelete}>
              Delete
            </Button>
          )}
          <Button
            className={styles.btnPrimary}
            onClick={() => save()}
            loading={saving}
            disabled={!canSave}
          >
            {existing ? 'Save' : 'Create package'}
          </Button>
        </Space>
      </div>
      </motion.div>

      <motion.div variants={itemVariants}>
      <Input
        placeholder="Short description for candidates…"
        value={draft.description}
        onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
        className={styles.descInput}
      />

      <div className={styles.pkgEditorLayout}>
        <div className={styles.pkgMain}>
          <div className={styles.toggleRow}>
            <div>
              <Text strong className={styles.toggleLabel}>
                Coaching
              </Text>
              <Text className={styles.toggleHint}>
                Live feedback and follow-up conversation with the coach after each answer. When
                off, candidates move straight to the next question with no in-interview coaching.
              </Text>
            </div>
            <Switch
              checked={draft.coachingEnabled}
              onChange={(checked) => setDraft((d) => ({ ...d, coachingEnabled: checked }))}
            />
          </div>
          <div className={styles.sectionTitle}>Credits &amp; pricing</div>
          <p className={styles.sectionNote}>
            Credits are scoped to each candidate. Package price is what the candidate pays; total
            credits are what their balance receives in return.
          </p>
          <div className={styles.creditsGrid}>
            <CreditBox
              label="Package Price"
              icon={<Tag style={{ width: '1em', height: '1em' }} />}
              tint="purple"
              value={priceText}
              unit={`$ ${APP_CURRENCY}`}
              onChange={(raw) => {
                const cleaned = raw.replace(/[^0-9.]/g, '');
                const firstDotIndex = cleaned.indexOf('.');
                const normalized =
                  firstDotIndex === -1
                    ? cleaned
                    : cleaned.slice(0, firstDotIndex + 1) +
                      cleaned.slice(firstDotIndex + 1).replace(/\./g, '');
                setPriceText(normalized);
                const parsed = Math.max(0, parseFloat(normalized) || 0);
                setDraft((d) => ({ ...d, unlockPriceDollars: parsed }));
              }}
              onBlur={() => setPriceText(draft.unlockPriceDollars.toFixed(2))}
            />
            <CreditBox
              label="Total Credits"
              icon={<Database style={{ width: '1em', height: '1em' }} />}
              tint="periwinkle"
              value={draft.unlockIncludedCredits.toString()}
              unit="credits"
              onChange={(raw) => setDraft((d) => ({ ...d, unlockIncludedCredits: digitsOnly(raw) }))}
            />
            <CreditBox
              label="Cost per Interview"
              icon={<Zap style={{ width: '1em', height: '1em' }} />}
              tint="green"
              value={draft.interviewCostCredits.toString()}
              unit="credits"
              onChange={(raw) => setDraft((d) => ({ ...d, interviewCostCredits: digitsOnly(raw) }))}
            />
          </div>

          <hr className={styles.divider} />

          <div className={styles.sectionTitle} style={{ marginBottom: 4 }}>
            Question set
          </div>
          <p className={styles.sectionNote}>
            Select the question set candidates practise when they unlock this package.
          </p>

          <div className={styles.toolbar} style={{ marginBottom: 16 }}>
            <div className={styles.search}>
              <Search className={styles.searchIcon} style={{ width: '1em', height: '1em' }} />
              <Input
                placeholder="Search question sets…"
                value={pickSearch}
                onChange={(e) => {
                  setPickSearch(e.target.value);
                  setPickPage(0);
                }}
                className={styles.searchInput}
              />
            </div>
          </div>

          {pickerItems.length === 0 ? (
            <div className={styles.empty}>
              <div className={styles.emptyTitle}>
                {questionSets.length ? 'No question sets match' : 'No question sets yet'}
              </div>
              <div className={styles.emptySub}>
                {questionSets.length ? (
                  'Adjust the search.'
                ) : (
                  <>
                    Create a{' '}
                    <a onClick={() => navigate(ROUTES.ADMIN_QUESTION_SET_NEW)}>Question Set</a> first,
                    then assign it here.
                  </>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className={styles.cardGrid}>
                {pickerItems.map((qs) => {
                  const selected = draft.questionSetId === qs.id;
                  const stats = questionSetStats(qs);
                  return (
                    <div
                      key={qs.id}
                      className={`${styles.tmplPickCard} ${selected ? styles.tmplPickSelected : ''}`}
                      onClick={() =>
                        setDraft((d) => ({ ...d, questionSetId: selected ? null : qs.id }))
                      }
                      role="button"
                      tabIndex={0}
                    >
                      <div className={styles.cardTitleRow}>
                        <div className={styles.cardTitle}>
                          {qs.name}
                          {qs.htsQuestionnaireId !== null && <span className={styles.htsBadge}>HTS</span>}
                        </div>
                      </div>
                      <div className={styles.cardMetaValue}>
                        {stats.questions} question{stats.questions !== 1 ? 's' : ''}
                      </div>
                      <div className={styles.cardMetaValue}>
                        {stats.competencies} competenc{stats.competencies !== 1 ? 'ies' : 'y'}
                      </div>
                      <div className={styles.tmplPickFooter}>
                        <Button
                          type={selected ? 'primary' : 'default'}
                          className={selected ? styles.btnPrimary : styles.btnSecondary}
                          icon={selected ? <Check style={{ width: '1em', height: '1em' }} /> : undefined}
                          block
                        >
                          {selected ? 'Selected' : 'Use this question set'}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {filteredQuestionSets.length > PICKER_PER_PAGE && (
                <Pager
                  page={pickerPage}
                  pageCount={pickerPages}
                  from={pickerFrom}
                  to={pickerTo}
                  total={pickerTotal}
                  onChange={setPickPage}
                />
              )}
            </>
          )}

          {existing && (
            <>
              <hr className={styles.divider} />
              <AccessCodesCard packageId={existing.id} />
            </>
          )}
        </div>

        <div className={styles.pkgSummaryPanel}>
          <div className={styles.pkgSummaryTitle}>Package summary</div>

          <div className={styles.pkgSummarySection}>
            <div className={styles.pkgSummarySectionLabel}>PRICING & CREDITS</div>
            <div className={styles.pkgSummaryRow}>
              <span className={styles.pkgSummaryLabel}>Package Price</span>
              <span className={styles.pkgSummaryVal}>
                ${draft.unlockPriceDollars.toFixed(2)} <span className={styles.pkgCr}>{APP_CURRENCY}</span>
              </span>
            </div>
            <div className={styles.pkgSummaryRow}>
              <span className={styles.pkgSummaryLabel}>Total credits</span>
              <span className={styles.pkgSummaryVal}>
                {draft.unlockIncludedCredits} <span className={styles.pkgCr}>cr</span>
              </span>
            </div>
            <div className={styles.pkgSummaryRow} style={{ marginBottom: 0 }}>
              <span className={styles.pkgSummaryLabel}>Cost per Interview</span>
              <span className={styles.pkgSummaryVal}>
                {draft.interviewCostCredits} <span className={styles.pkgCr}>cr</span>
              </span>
            </div>
          </div>

          <div className={styles.pkgSummarySection}>
            <div className={styles.pkgSummarySectionLabel}>QUESTION SET</div>
            {selectedQuestionSet ? (
              <>
                <div className={styles.pkgSummarySetName}>
                  {selectedQuestionSet.name}
                  {selectedQuestionSet.htsQuestionnaireId !== null && (
                    <span className={styles.htsBadge}>HTS</span>
                  )}
                </div>
                <div className={styles.pkgSummaryMuted}>
                  {questionSetStats(selectedQuestionSet).questions} question
                  {questionSetStats(selectedQuestionSet).questions !== 1 ? 's' : ''}
                </div>
                <div className={styles.pkgSummaryMuted}>
                  {questionSetStats(selectedQuestionSet).competencies} competenc
                  {questionSetStats(selectedQuestionSet).competencies !== 1 ? 'ies' : 'y'}
                </div>
              </>
            ) : (
              <div className={styles.pkgSummaryEmpty}>No question set selected</div>
            )}
          </div>

          <div>
            <Text className={styles.pkgSummaryMuted}>Price</Text>
            <div className={styles.pkgSummaryPriceRow}>
              <span className={styles.pkgSummaryPrice}>
                ${draft.unlockPriceDollars.toFixed(2)} <span className={styles.pkgCr}>{APP_CURRENCY}</span>
              </span>
            </div>
          </div>

          {existing && (
            <div className={styles.pkgSummarySection}>
              <div className={styles.pkgSummarySectionLabel}>PURCHASE LINK</div>
              <p className={styles.pkgSummaryMuted}>
                Share this link on external sites so customers can buy this package. It always
                reflects the current price.
              </p>
              <Tooltip title="Copy purchase link">
                <div
                  className={styles.buyLinkChip}
                  role="button"
                  tabIndex={0}
                  aria-label="Copy purchase link"
                  onClick={copyBuyLink}
                >
                  <Link className={styles.buyLinkIcon} style={{ width: '1em', height: '1em' }} />
                  <span className={styles.buyLinkText}>{buildPackageBuyUrl(existing.id)}</span>
                  <Copy className={styles.buyLinkCopy} style={{ width: '1em', height: '1em' }} />
                </div>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
      </motion.div>
    </motion.div>
  );
};

export default PackageEditor;
